// Répartition des commandes: camions proposés, reste à affecter et couverture.
// Ports de AllocationService.find_available_trucks_for_order,
// TripRepository.find_available_for_allocation et
// core/domain_readiness.build_dispatch_readiness.
import { SEQUENCE_STATUTS_TRAJET, STATUTS_AFFECTABLES, calculerProgressionCommande } from './progressionCommande';

const STATUTS_SOLDES = new Set(['DELIVERED', 'COMPLETED', 'CANCELLED']);
const STATUTS_AFFECTABLES_TRAJET = new Set(SEQUENCE_STATUTS_TRAJET.filter((s) => !STATUTS_SOLDES.has(s)));
const INDEX_PIVOT = SEQUENCE_STATUTS_TRAJET.indexOf('CLEARED_CUSTOMS_ENTRY');
const PHASE_LIVRAISON = new Set(SEQUENCE_STATUTS_TRAJET.slice(INDEX_PIVOT + 1));
const PHASE_IMPORT = new Set(SEQUENCE_STATUTS_TRAJET.slice(0, INDEX_PIVOT + 1));
const LIBELLES_PHASE = { IMPORTATION: 'Importation', LIVRAISON: 'Livraison', CLOTURE: 'Clôturé' };
const CITERNE_PAR_DEFAUT = 40000;

const num = (valeur) => {
  const n = Number.parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
};

export const phaseDuTrajet = (statut) => {
  if (PHASE_LIVRAISON.has(statut)) return 'LIVRAISON';
  if (PHASE_IMPORT.has(statut)) return 'IMPORTATION';
  return 'CLOTURE';
};

const plier = (texte) =>
  String(texte ?? '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const destinationCorrespond = (lieu, pays) => {
  const l = plier(lieu);
  const p = plier(pays);
  return Boolean(l && p && l.includes(p));
};

const arrondir = (valeur, decimales = 2) => {
  const facteur = 10 ** decimales;
  return Math.round((valeur + Number.EPSILON) * facteur) / facteur;
};

export function trouverCamionsDisponibles({ quantiteCommande, destination = null, restant = null, trajets, camions, chauffeurs }) {
  const camionParId = Object.fromEntries(camions.map((c) => [String(c.id), c]));
  const chauffeurParId = Object.fromEntries(chauffeurs.map((c) => [String(c.id), c]));
  const cible = num(restant !== null && restant !== undefined ? restant : quantiteCommande);

  const proposes = [];
  for (const trajet of trajets) {
    if (trajet.deleted || !STATUTS_AFFECTABLES_TRAJET.has(trajet.status)) continue;
    const camion = camionParId[String(trajet.truck_id)];
    const mesure = num(trajet.fuel_quantity_real);
    let reference;
    let estMesure;
    if (mesure > 0) {
      reference = mesure;
      estMesure = true;
    } else {
      reference = num(trajet.allocated_quantity) || (trajet.truck_id ? num(camion?.capacity_liters) : 0);
      estMesure = false;
    }
    const cache = trajet.remaining_liters;
    const disponible = cache === null || cache === undefined ? reference : num(cache);
    if (disponible < 1) continue;
    proposes.push({ trajet, camion, disponible, estMesure });
  }
  proposes.sort((a, b) => {
    const ca = String(a.trajet.created_at || '');
    const cb = String(b.trajet.created_at || '');
    if (ca !== cb) return ca < cb ? -1 : 1;
    return String(a.trajet.id) < String(b.trajet.id) ? -1 : 1;
  });

  const lignes = proposes.map(({ trajet, camion, disponible, estMesure }) => {
    const chauffeur = trajet.driver_id ? chauffeurParId[String(trajet.driver_id)] : null;
    const pourcentage = disponible > 0 ? (num(quantiteCommande) / disponible) * 100 : 0;
    const couverture = cible > 0 ? (disponible / cible) * 100 : null;
    const phase = phaseDuTrajet(trajet.status || '');
    return {
      trip_id: trajet.id,
      truck_id: trajet.truck_id,
      truck_matricule: camion?.matricule || 'Plaque non renseignée',
      truck_capacity: camion?.capacity_liters || 0,
      driver_name: chauffeur ? chauffeur.name ?? null : null,
      driver_phone: chauffeur ? chauffeur.phone ?? null : null,
      available_quantity: disponible,
      volume_is_measured: Boolean(estMesure),
      trip_status: trajet.status,
      phase,
      phase_label: LIBELLES_PHASE[phase] || phase,
      origin: trajet.origin ?? null,
      destination_country: trajet.destination_country ?? null,
      destination_match: destinationCorrespond(destination, trajet.destination_country),
      can_fulfill_order: disponible >= cible,
      allocation_percentage: arrondir(pourcentage, 2),
      coverage_percentage: couverture !== null ? arrondir(Math.min(100, couverture), 2) : null,
      score: couverture !== null ? Math.round(Math.min(100, couverture)) : null,
    };
  });
  lignes.sort((a, b) => {
    if (b.available_quantity !== a.available_quantity) return b.available_quantity - a.available_quantity;
    if (a.truck_matricule !== b.truck_matricule) return a.truck_matricule < b.truck_matricule ? -1 : 1;
    return String(a.trip_id) < String(b.trip_id) ? -1 : 1;
  });
  return lignes;
}

// --- build_dispatch_readiness -----------------------------------------------------------------

const ETIQUETTES_STATUT = {
  DRAFT: 'brouillon', PENDING: 'en attente', CONFIRMED: 'confirmée', ALLOCATED: 'affectée',
  IN_TRANSIT: 'en route', PARTIALLY_DELIVERED: 'livrée en partie', DELIVERED: 'livrée',
  INVOICED: 'facturée', PAID: 'payée', CANCELLED: 'annulée',
};
export const libelleStatutCommande = (statut) =>
  ETIQUETTES_STATUT[String(statut || '').toUpperCase()] || 'dans un état inconnu';

const espaces = (texte) => texte.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const litresTexte = (valeur) => `${espaces(String(Math.round(valeur)))} L`;
const accord = (nombre, singulier, pluriel) => {
  const valeur = Math.round((nombre || 0) * 10) / 10;
  let affiche;
  if (Number.isInteger(valeur)) affiche = espaces(String(valeur));
  else affiche = espaces(valeur.toFixed(1)).replace('.', ',');
  return `${affiche} ${Math.abs(valeur) < 2 ? singulier : pluriel}`;
};

const exigence = (cle, libelle, atteinte, detail = '') => ({ key: cle, label: libelle, met: Boolean(atteinte), detail });
const blocage = (cle, message, severite = 'warning') => ({ key: cle, message, severity: severite });

export function preparerRepartition({ commande, camionsDisponibles, autoriserPartiel = false, litresCiterne = null, affectations = null, trajets = null }) {
  const progression = calculerProgressionCommande(commande, affectations, trajets);
  const demande = progression.ordered_liters;
  const affecte = progression.allocated_liters;
  const livre = progression.delivered_liters;
  const restant = progression.to_allocate_liters;
  const capacite = camionsDisponibles.reduce((s, c) => s + num(c.available_quantity), 0);
  const statut = String(commande.status || '').toUpperCase();
  const statutValide = STATUTS_AFFECTABLES.has(statut) && !commande.delivery_closed_at;
  const unCamionSuffit = camionsDisponibles.some((c) => num(c.available_quantity) >= restant);

  const unite = num(litresCiterne) || num(commande.cistern_liters_snapshot) || CITERNE_PAR_DEFAUT;
  const couvrable = Math.min(capacite, restant);
  const decouvert = Math.max(0, restant - capacite);

  const exigences = [
    exigence('order_open', 'Commande ouverte', statutValide, libelleStatutCommande(statut)),
    exigence('remaining_volume', 'Volume restant à affecter', restant > 0, litresTexte(restant)),
    exigence('truck_capacity', 'Capacité camion suffisante', capacite >= restant && restant > 0, `${litresTexte(capacite)} disponibles`),
    exigence('allocation_split', 'Camion disponible', camionsDisponibles.length > 0, `${camionsDisponibles.length} camion(s)`),
  ];
  const blocages = [];
  if (!statutValide) {
    blocages.push(blocage('invalid_order_status', `Cette commande est ${libelleStatutCommande(statut)} : elle ne peut plus recevoir de camion.`, 'error'));
  }
  if (statutValide && restant <= 0) {
    blocages.push(
      blocage(
        'nothing_to_allocate',
        affecte > 0 || livre > 0 ? 'Cette commande est déjà entièrement affectée.' : "Il n'y a aucun volume à affecter sur cette commande.",
        'error',
      ),
    );
  }
  if (restant > 0 && camionsDisponibles.length === 0) {
    blocages.push(blocage('no_truck_available', 'Aucun camion libre pour le moment.', 'error'));
  } else if (restant > 0 && capacite < restant) {
    let message;
    if (autoriserPartiel) {
      const citernesRestantes = unite > 0 ? decouvert / unite : 0;
      message =
        `${accord(camionsDisponibles.length, 'camion couvre', 'camions couvrent')} ` +
        `${litresTexte(capacite)} sur ${litresTexte(restant)} — ` +
        `${accord(citernesRestantes, 'citerne restera', 'citernes resteront')} à affecter.`;
    } else {
      message =
        `Les camions libres couvrent ${litresTexte(capacite)} sur ${litresTexte(restant)} : ` +
        `${litresTexte(decouvert)} resteront à affecter à un autre camion.`;
    }
    blocages.push(blocage('partial_coverage', message, 'warning'));
  }

  return {
    domain: 'dispatch',
    ready: statutValide && restant > 0 && camionsDisponibles.length > 0,
    score: Math.round((100 * exigences.filter((e) => e.met).length) / exigences.length),
    requirements: exigences,
    blocking_reasons: blocages,
    progress: progression,
    metrics: {
      requested_liters: demande,
      allocated_liters: affecte,
      delivered_liters: livre,
      remaining_liters: restant,
      available_capacity_liters: capacite,
      available_truck_count: camionsDisponibles.length,
      single_truck_can_cover: unCamionSuffit,
      allow_partial_dispatch: Boolean(autoriserPartiel),
      coverable_liters: couvrable,
      uncovered_liters: decouvert,
      cistern_liters: unite,
      remaining_cisterns: unite > 0 ? arrondir(restant / unite, 2) : 0,
      coverable_cisterns: unite > 0 ? arrondir(couvrable / unite, 2) : 0,
    },
    business_context: {
      why: 'Le dispatch doit garantir que le volume client est couvert sans sur-allocation ni camion inadapté.',
      what_changes: "L’allocation lie la commande au trajet et prépare livraison, stock et facturation.",
      what_blocks: blocages.map((b) => b.message),
      next_action: 'Sélectionner les camions proposés puis valider seulement le volume restant.',
    },
    action_contract: {
      action: 'allocate_order_to_trip',
      preconditions: ['commande ouverte', 'trajet allouable', 'volume restant positif', 'au moins un camion libre'],
      expected_effect: 'Lier commande et trajet avec quantité allouée unique.',
      linked_effects: ['planning livraison', 'stock à livrer', 'readiness facture'],
      idempotency: 'allocation_unique_par_commande_trajet',
      audit: 'dispatch_allocation_audit',
      failure_message: 'Affectation bloquée si l’état, le volume ou le camion ne sont pas cohérents.',
      next_step: 'Contrôler la livraison puis réconcilier le volume.',
    },
  };
}
